import Big, { RoundingMode as BigJsRoundingMode } from 'big.js'
import { isNil } from 'lodash-es'

type Fn<A extends any[], R> = (...args: [...A]) => R

export const swallowPrecisionError = <A extends any[], R>(fn: Fn<A, R>): Fn<A, R | undefined> => (...args) => {
  try {
    return fn(...args)
  } catch (e) {
    if (e instanceof PrecisionLossError) {
      return
    }
    throw e
  }
}

export class PrecisionLossError extends Error {
  constructor(firstValue: string, secondValue: string) {
    super()
    this.message = `Precision loss in number conversion ${firstValue} vs ${secondValue}`
  }
}

export function toNumber(big: Big): number {
  const bigStr = big.toString()
  const result = Number(bigStr)

  if (big.eq(Big(result)) === false) {
    throw new PrecisionLossError(bigStr, result.toString())
  }

  return result
}

export function parse(value: string): number {
  if (value === '.' || value === '-' || value === '-.' || value === '+' || value === '+.') {
    return 0
  } else {
    // Big.js does not current handle values prefixed with +, so take account for it here
    const big = value[0] === '+' ? Big(value.slice(1)) : Big(value)
    return toNumber(big)
  }
}

export function add(num1: number, num2: number): number {
  const big1 = NullSafeBig(num1)
  const big2 = NullSafeBig(num2)
  return toNumber(big1.add(big2))
}

export function sum(nums: number[]): number {
  const bigResult = nums.reduce((result, num) => {
    const big = NullSafeBig(num)
    return result.add(big)
  }, Big(0))
  return toNumber(bigResult)
}

export function subtract(num1: number, num2: number): number {
  const big1 = NullSafeBig(num1)
  const big2 = NullSafeBig(num2)
  return toNumber(big1.sub(big2))
}

export function multiply(num1: number, num2: number): number {
  const big1 = NullSafeBig(num1)
  const big2 = NullSafeBig(num2)
  return toNumber(big1.times(big2))
}

export function divide(num1: number, num2: number): number {
  const big1 = NullSafeBig(num1)
  const big2 = NullSafeBig(num2)
  return toNumber(big1.div(big2))
}

/**
 * some type of divisions will always provoke a precission error
 * if we are only using those numbers to display on the UI
 * we are not really taking advantage of the precission
 * so we can use this method for those situations
 **/
export function unsafeDivide(num1: number, num2: number): number {
  const big1 = NullSafeBig(num1)
  const big2 = NullSafeBig(num2)
  return Number(
    big1
      .div(big2)
      .toFixed(3)
      .toString()
  )
}

export function mod(num1: number, num2: number): number {
  const big1 = NullSafeBig(num1)
  const big2 = NullSafeBig(num2)
  return toNumber(big1.mod(big2))
}

export function abs(num: number): number {
  const big = NullSafeBig(num)
  return toNumber(big.abs())
}

export function shift(num: number, exponent: number): number {
  const big = NullSafeBig(num)
  big.e = big.e + exponent
  return toNumber(big)
}

export const RoundingMode = {
  Down: 0 as BigJsRoundingMode,
  HalfUp: 1 as BigJsRoundingMode,
  HalfEven: 2 as BigJsRoundingMode,
  Up: 3 as BigJsRoundingMode
}

export function round(num: number, toNearest: number, roundingMode: BigJsRoundingMode): number {
  if (isNil(num) || isNil(toNearest)) {
    return 0
  }
  const bigNum = Big(num)
  const bigToNearest = Big(toNearest)
  const bigRemainder = bigNum.mod(bigToNearest)

  if (roundingMode === RoundingMode.Down) {
    return bigRemainder.gte(0) ? toNumber(bigNum.sub(bigRemainder)) : toNumber(bigNum.sub(bigRemainder).sub(bigToNearest))
  } else if (roundingMode === RoundingMode.Up) {
    return bigRemainder.lte(0) ? toNumber(bigNum.sub(bigRemainder)) : toNumber(bigNum.sub(bigRemainder).add(bigToNearest))
  } else {
    throw new Error(`Unhandled rounding mode [${roundingMode}]`)
  }
}

export function getDecimalPlaces(num: number): number {
  const big = NullSafeBig(num)
  return big.c.length - big.e - 1
}

export function getMin(min: number | undefined, maxDp: number | undefined): number | undefined {
  if (isNil(min) || isNil(maxDp)) {
    return min
  }
  return toNumber(Big(min).round(maxDp, RoundingMode.Up))
}

export function getMax(max: number | undefined, maxDp: number | undefined): number | undefined {
  if (isNil(max) || isNil(maxDp)) {
    return max
  }
  return toNumber(Big(max).round(maxDp, RoundingMode.Down))
}

function NullSafeBig(num: number) {
  return isNil(num) ? Big(0) : Big(num)
}
